import React, { useState } from "react";

import { onKeyPressEvent } from "@components/board/board";
import ContentCapsLockOn from "@components/content-capsLock-on/content-capsLock-on";
import ContentChangeLang from "@components/content-change-lang/content-change-lang";
import ContentDisabledShift from "@components/content-disabled-shift/content-disabled-shift";
import ContentRemoveAds from "@components/content-remove-ads/content-remove-ads";
import { useRemoveAdsPopup } from "@components/content-remove-ads/hooks/use-remove-ads-popup";
import MainModal from "@components/modal/main-modal";

import {
  isInLowercase,
  isInUppercase,
  isLetter,
  isNotSpace,
} from "@helpers/helpers";
import { keyboards } from "@constants/keyboards";
import {
  HelperModalType,
  Keyboard,
  Keyboards,
  KeyPressType,
} from "@typings/global.types";

import { useGetExerciseEntrypointQuery } from "../../api/exercise-entrypoint";

type WrapperHelperModalsPropsType = {
  modalType: HelperModalType | null;
  isAuthorized: boolean;
  isPromotion?: boolean;
  closeModalHandler: () => void;
  onEnter?: () => void;
  onExit?: () => void;
};
function WrapperHelperModals({
  modalType,
  isAuthorized,
  isPromotion,
  closeModalHandler,
  onEnter,
  onExit,
}: WrapperHelperModalsPropsType) {
  const { hidePopup: hideRemoveAdsPopup } = useRemoveAdsPopup();
  const wasModalClosed =
    window.sessionStorage.getItem("modalClosed") === "true";

  const getModalContent = (modalType: HelperModalType) => {
    switch (modalType) {
      case HelperModalType.enabledCapslock:
        return <ContentCapsLockOn onClick={() => closeModalHandler()} />;
      case HelperModalType.wrongLanguage:
        return (
          <ContentChangeLang
            onClick={() => closeModalHandler()}
            isAuthorized={isAuthorized}
          />
        );
      case HelperModalType.disabledShift:
        return <ContentDisabledShift onClick={() => closeModalHandler()} />;
      case HelperModalType.removeAds:
        return <ContentRemoveAds />;
      default:
        return null;
    }
  };

  const closeHandler = () => {
    if (hideRemoveAdsPopup && modalType === HelperModalType.removeAds) {
      hideRemoveAdsPopup();
    }
    closeModalHandler();
  };

  if (wasModalClosed && modalType === HelperModalType.removeAds) {
    return null;
  }

  return (
    <MainModal
      show={!!modalType}
      onClick={closeHandler}
      showCloseBtn
      onEnter={onEnter}
      onExit={onExit}
      isRemoveAdsModal={modalType === HelperModalType.removeAds && isPromotion}
      isBirthdayRemoveAdsModal={
        modalType === HelperModalType.removeAds && window.reactApp.isBirthDay
      }
    >
      {getModalContent(modalType!)}
    </MainModal>
  );
}

export default WrapperHelperModals;

/**
 * Функція для визначення типу модального вікна,
 * яке треба показати,
 * в залежності від введеного та очікуваного символу
 * @param event
 * @param expectedChar
 * @param keyboardLayout
 * @param isStartExerciseStarted
 */
export const detectModalType = (
  event: onKeyPressEvent,
  expectedChar: string,
  keyboardLayout: keyof Keyboards,
  isStartExerciseStarted: boolean,
): HelperModalType | null => {
  if (detectCapsLockModal(event, expectedChar)) {
    return HelperModalType.enabledCapslock;
  }

  // модалку з капслоком і шифтом показуємо лише на початку вправи
  if (!isStartExerciseStarted) {
    if (detectWrongLanguageModal(event, keyboardLayout)) {
      return HelperModalType.wrongLanguage;
    }
    if (detectDisabledShiftModal(event, expectedChar)) {
      return HelperModalType.disabledShift;
    }
  }

  return null;
};

const detectCapsLockModal = (event: onKeyPressEvent, expectedChar: string) => {
  return (
    isLetter(event.data) &&
    event.capslockModifier && // Коли натиснута будь-яка клавіша з увімкненим СapsLock
    expectedChar === expectedChar.toLowerCase() // Якщо літера, яку очікуємо - маленька
  );
};

const detectDisabledShiftModal = (
  event: onKeyPressEvent,
  expectedChar: string,
) => {
  return (
    isLetter(event.data) &&
    isInLowercase(event.data) &&
    !event.shiftKey &&
    isLetter(expectedChar) &&
    isInUppercase(expectedChar)
  );
};

const detectWrongLanguageModal = (
  event: onKeyPressEvent,
  keyboardLayout: keyof Keyboards,
) => {
  const keyboard: Keyboard | undefined = keyboards[keyboardLayout];
  if (!keyboard) return false;
  const defaultAcceptedKeys =
    " ʼ‘’'“”„“«»`´1234567890-=[]\\;',./~!@#$%^&*()_+{}|:\"<>?% ";
  const allAcceptedKeys = [
    ...defaultAcceptedKeys.split(""),
    ...(keyboard?.acceptedKeys ?? "").split(""),
    ...(keyboard?.acceptedWithDeadKey ?? "").split(""),
  ];

  const isKeyAccepted = allAcceptedKeys.includes(event.data);

  return !isKeyAccepted; // Коли літери немає в масиві літер, мають бути в клавіатурі
};
